import { z } from "zod";

const FETCH_TIMEOUT_MS = 10_000;
// Guard against materializing large images in heap before the Drive upload.
// LINE allows up to 10 MB; cap at 5 MB to leave headroom for concurrent calls.
export const MAX_MESSAGE_CONTENT_BYTES = 5 * 1024 * 1024;

export class LineApiError extends Error {
  constructor(
    public readonly endpoint: string,
    public readonly status: number,
    public readonly body: string,
  ) {
    super(`${endpoint} failed: ${status} ${body.slice(0, 200)}`);
    this.name = "LineApiError";
  }
}

export class LineContentTooLargeError extends Error {
  constructor(public readonly contentLength: number) {
    super(
      `fetchMessageContent: content too large (${contentLength} bytes, max ${MAX_MESSAGE_CONTENT_BYTES})`,
    );
    this.name = "LineContentTooLargeError";
  }
}

export interface LineMessage {
  type: "text";
  text: string;
}

export interface ReplyMessageInput {
  replyToken: string;
  messages: LineMessage[];
  channelAccessToken: string;
}

export async function replyMessage(input: ReplyMessageInput): Promise<void> {
  const res = await fetch("https://api.line.me/v2/bot/message/reply", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${input.channelAccessToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ replyToken: input.replyToken, messages: input.messages }),
    signal: AbortSignal.timeout(FETCH_TIMEOUT_MS),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new LineApiError("replyMessage", res.status, text);
  }
}

export interface GetProfileInput {
  userId: string;
  channelAccessToken: string;
}

const LineProfileSchema = z.object({
  displayName: z.string(),
  pictureUrl: z.string().optional(),
  language: z.string().optional(),
});
export type LineProfile = z.infer<typeof LineProfileSchema>;

export async function getProfile(input: GetProfileInput): Promise<LineProfile> {
  const res = await fetch(
    `https://api.line.me/v2/bot/profile/${encodeURIComponent(input.userId)}`,
    {
      headers: { Authorization: `Bearer ${input.channelAccessToken}` },
      signal: AbortSignal.timeout(FETCH_TIMEOUT_MS),
    },
  );
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`getProfile failed: ${res.status} ${text}`);
  }
  return LineProfileSchema.parse(await res.json());
}

export interface FetchMessageContentInput {
  messageId: string;
  channelAccessToken: string;
}

export interface MessageContent {
  bytes: Uint8Array;
  contentType: string;
}

export async function fetchMessageContent(
  input: FetchMessageContentInput,
): Promise<MessageContent> {
  const res = await fetch(
    `https://api-data.line.me/v2/bot/message/${encodeURIComponent(input.messageId)}/content`,
    {
      headers: { Authorization: `Bearer ${input.channelAccessToken}` },
      signal: AbortSignal.timeout(FETCH_TIMEOUT_MS),
    },
  );
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`fetchMessageContent failed: ${res.status} ${text}`);
  }
  // Fast-fail on declared Content-Length when present — skips reading the
  // body entirely for obviously-too-large responses.
  const rawContentLength = res.headers.get("Content-Length");
  if (rawContentLength !== null) {
    const contentLength = Number(rawContentLength);
    if (!Number.isNaN(contentLength) && contentLength > MAX_MESSAGE_CONTENT_BYTES) {
      throw new LineContentTooLargeError(contentLength);
    }
  }
  // Stream the body so chunked-encoded responses (no Content-Length) can't
  // bypass the cap. Without this, a 10 MB image with chunked transfer would
  // be fully buffered into the Worker isolate's 128 MB heap before any
  // guard fired — concurrent uploads could exhaust it.
  const bytes = await readBodyBounded(res, MAX_MESSAGE_CONTENT_BYTES);
  return {
    bytes,
    contentType: res.headers.get("Content-Type") ?? "application/octet-stream",
  };
}

async function readBodyBounded(res: Response, maxBytes: number): Promise<Uint8Array> {
  // Workers' fetch always exposes a body stream; this branch is defensive.
  if (!res.body) return new Uint8Array(await res.arrayBuffer());
  const reader = res.body.getReader();
  const chunks: Uint8Array[] = [];
  let total = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    total += value.byteLength;
    if (total > maxBytes) {
      await reader.cancel().catch(() => {});
      throw new LineContentTooLargeError(total);
    }
    chunks.push(value);
  }
  const out = new Uint8Array(total);
  let offset = 0;
  for (const chunk of chunks) {
    out.set(chunk, offset);
    offset += chunk.byteLength;
  }
  return out;
}
